/**
 * Review Model - Centralized schema definition for course reviews
 * This is the single source of truth for review structure
 */

/**
 * Review schema definition
 * All review objects should conform to this structure
 */
export const ReviewSchema = {
  // Required fields
  id: {
    type: String,
    required: true,
    description: 'Unique identifier for the review'
  },
  courseId: {
    type: String,
    required: true,
    description: 'ID of the course being reviewed'
  },
  userId: {
    type: String,
    required: true,
    description: 'ID of the user who wrote the review'
  },
  educationalContent: {
    type: Number,
    required: true,
    description: 'Rating for educational content (1-5 stars)',
    min: 1,
    max: 5,
    integer: true
  },
  engagement: {
    type: Number,
    required: true,
    description: 'Rating for engagement (1-5 stars)',
    min: 1,
    max: 5,
    integer: true
  },
  overallValue: {
    type: Number,
    required: true,
    description: 'Rating for overall value (1-5 stars)',
    min: 1,
    max: 5,
    integer: true
  },
  reviewText: {
    type: String,
    required: true,
    description: 'Text review/thoughts about the course'
  },

  // System fields
  createdAt: {
    type: String,
    required: false,
    description: 'ISO 8601 timestamp of when the review was created'
  },
  updatedAt: {
    type: String,
    required: false,
    description: 'ISO 8601 timestamp of when the review was last updated'
  }
}

/**
 * Create a new review object with default values
 * @param {Object} data - Review data
 * @returns {Object} Review object
 */
export function createReview(data = {}) {
  const now = new Date().toISOString()
  
  return {
    id: data.id || null,
    courseId: data.courseId || data.course_id || null,
    userId: data.userId || data.user_id || null,
    educationalContent: data.educationalContent !== undefined ? Number(data.educationalContent) : (data.educational_content !== undefined ? Number(data.educational_content) : null),
    engagement: data.engagement !== undefined ? Number(data.engagement) : null,
    overallValue: data.overallValue !== undefined ? Number(data.overallValue) : (data.overall_value !== undefined ? Number(data.overall_value) : null),
    reviewText: data.reviewText || data.review_text || '',
    createdAt: data.createdAt || data.created_at || now,
    updatedAt: data.updatedAt || data.updated_at || now
  }
}

/**
 * Convert review object from Rails API format (snake_case) to frontend format (camelCase)
 * @param {Object} railsReview - Review object from Rails API
 * @returns {Object} Review object in frontend format
 */
export function fromRailsFormat(railsReview) {
  if (!railsReview) return null
  
  const review = {
    id: railsReview.id,
    courseId: railsReview.course_id,
    userId: railsReview.user_id,
    educationalContent: railsReview.educational_content ? Number(railsReview.educational_content) : null,
    engagement: railsReview.engagement ? Number(railsReview.engagement) : null,
    overallValue: railsReview.overall_value ? Number(railsReview.overall_value) : null,
    reviewText: railsReview.review_text,
    createdAt: railsReview.created_at,
    updatedAt: railsReview.updated_at
  }
  
  // Transform nested user object if present
  if (railsReview.user) {
    review.user = {
      firstName: railsReview.user.first_name || railsReview.user.firstName,
      lastInitial: railsReview.user.last_initial || railsReview.user.lastInitial,
      experienceLevel: railsReview.user.experience_level || railsReview.user.experienceLevel
    }
  }
  
  return review
}

/**
 * Convert review object from frontend format (camelCase) to Rails API format (snake_case)
 * @param {Object} review - Review object in frontend format
 * @returns {Object} Review object in Rails API format
 */
export function toRailsFormat(review) {
  if (!review) return null
  
  return {
    id: review.id,
    course_id: review.courseId,
    user_id: review.userId,
    educational_content: review.educationalContent,
    engagement: review.engagement,
    overall_value: review.overallValue,
    review_text: review.reviewText
  }
}

/**
 * Validate a review object against the schema
 * @param {Object} review - Review object to validate
 * @returns {Object} Validation result with isValid and errors
 */
export function validateReview(review) {
  const errors = []

  // Required fields
  if (!review.courseId) {
    errors.push('Course ID is required')
  }

  console.log('review', review)

  if (review.educationalContent === null || review.educationalContent === undefined) {
    errors.push('Educational content rating is required')
  } else if (!Number.isInteger(review.educationalContent) || review.educationalContent < 1 || review.educationalContent > 5) {
    errors.push('Educational content rating must be between 1 and 5')
  }
  if (review.engagement === null || review.engagement === undefined) {
    errors.push('Engagement rating is required')
  } else if (!Number.isInteger(review.engagement) || review.engagement < 1 || review.engagement > 5) {
    errors.push('Engagement rating must be between 1 and 5')
  }
  if (review.overallValue === null || review.overallValue === undefined) {
    errors.push('Overall value rating is required')
  } else if (!Number.isInteger(review.overallValue) || review.overallValue < 1 || review.overallValue > 5) {
    errors.push('Overall value rating must be between 1 and 5')
  }
  if (!review.reviewText || review.reviewText.trim() === '') {
    errors.push('Review text is required')
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Calculate average rating from a review
 * @param {Object} review - Review object
 * @returns {number} Average rating (1-5)
 */
export function getAverageRating(review) {
  const ratings = [review.educationalContent, review.engagement, review.overallValue]
  const sum = ratings.reduce((acc, val) => acc + val, 0)
  return Math.round((sum / ratings.length) * 10) / 10 // Round to 1 decimal place
}

